import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, BarChart3, Search, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { orderStatsAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const REG_STYLE = {
    'stable': 'bg-emerald-100 text-emerald-700',
    'variable': 'bg-amber-100 text-amber-700',
    'irrégulier': 'bg-red-100 text-red-700',
};

const buildColumns = (source) => {
    const rec = source === 'receptions';
    return [
        { key: 'product_name', label: 'Produit', align: 'left' },
        { key: 'supplier_name', label: 'Fournisseur', align: 'left' },
        { key: 'site_name', label: 'Site', align: 'left' },
        { key: 'cadence_weeks', label: 'Cadence', align: 'right', title: `Écart moyen entre semaines de ${rec ? 'réception' : 'commande'}` },
        { key: 'avg_qty_per_order', label: rec ? 'Qté moy/sem. reçue' : 'Qté moy/cde', align: 'right', title: `Quantité moyenne par semaine de ${rec ? 'réception' : 'commande'}` },
        { key: 'weekly_avg_qty', label: 'Qté hebdo moy', align: 'right', title: `Quantité totale ÷ semaines écoulées depuis la 1ère ${rec ? 'réception' : 'commande'}` },
        { key: 'qty_per_1000', label: 'Ratio /1000 € CA', align: 'right', title: `Quantité ${rec ? 'reçue' : 'commandée'} pour 1 000 € de CA réalisé sur la période` },
        { key: 'regularity', label: 'Régularité', align: 'center' },
        { key: 'n_orders', label: rec ? 'Nb récep.' : 'Nb cdes', align: 'right' },
        { key: 'last_week', label: rec ? 'Dern. récep.' : 'Dern. cde', align: 'right' },
    ];
};

function fmtPurchase(qtyOrderUnits, r, full = true) {
    if (qtyOrderUnits == null) return null;
    const pq = r.purchase_pack_qty || 1;
    // < 1 unité d'achat : afficher directement l'unité intermédiaire (ex. 6 Sachet plutôt que 0,5 Carton de 12 Sachet)
    if (qtyOrderUnits < 1 && pq > 1 && r.purchase_billing_unit) {
        const q = Math.round(qtyOrderUnits * pq * 10) / 10;
        return `≈ ${q} ${r.purchase_billing_unit}`;
    }
    return `≈ ${qtyOrderUnits} ${full ? r.purchase_label : r.purchase_unit}`;
}

function RatioRow({ r }) {
    return (
        <tr className="border-b border-slate-100 hover:bg-slate-50/60">
            <td className="px-2 py-1 text-slate-700 font-medium max-w-[260px]">
                <span className="line-clamp-1" title={r.product_name}>{r.product_name}</span>
                {r.is_consumable && <Badge className="bg-slate-200 text-slate-600 text-[9px] ml-1">Conso/Génipa</Badge>}
            </td>
            <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{r.supplier_name}</td>
            <td className="px-2 py-1 text-slate-600 whitespace-nowrap">{(r.site_name || '?').replace('FE ', '')}</td>
            <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">
                {r.cadence_weeks == null
                    ? <span className="text-slate-300" title="Une seule commande sur la période">1 seule cde</span>
                    : r.cadence_weeks <= 1.2
                        ? <span className="text-emerald-600 font-semibold">chaque sem.</span>
                        : `~${r.cadence_weeks} sem.`}
            </td>
            <td className="px-2 py-1 text-right tabular-nums">
                {r.avg_qty_per_order}{r.order_unit ? <span className="text-slate-400"> {r.order_unit}</span> : ''}
                {r.avg_purchase_qty != null && (
                    <div className="text-[9px] text-slate-400" title={`Unité d'achat habituelle : ${r.purchase_sp_name || ''} (${r.purchase_label})`}>
                        {fmtPurchase(r.avg_purchase_qty, r)}
                    </div>
                )}
            </td>
            <td className="px-2 py-1 text-right tabular-nums text-slate-600">
                {r.weekly_avg_qty}
                {r.weekly_purchase_qty != null && (
                    <div className="text-[9px] text-slate-400">{fmtPurchase(r.weekly_purchase_qty, r, false)}</div>
                )}
            </td>
            <td className="px-2 py-1 text-right tabular-nums font-semibold text-indigo-700">
                {r.qty_per_1000 != null ? r.qty_per_1000 : <span className="text-slate-300 font-normal">—</span>}
            </td>
            <td className="px-2 py-1 text-center">
                {r.regularity
                    ? <Badge className={`text-[9px] ${REG_STYLE[r.regularity]}`}>{r.regularity}</Badge>
                    : <span className="text-slate-300 text-[10px]" title="Moins de 3 semaines de commande">peu de données</span>}
            </td>
            <td className="px-2 py-1 text-right tabular-nums text-slate-500" title={`Total dépensé : ${formatCurrency(r.total_spent)}`}>{r.n_orders}</td>
            <td className="px-2 py-1 text-right tabular-nums text-slate-500">S{r.last_week}</td>
        </tr>
    );
}

export default function OrderRatiosTab() {
    const { user } = useAuth();
    const allowedIds = user?.allowed_sites?.length ? user.allowed_sites : null;
    const [mode, setMode] = useState('industrial'); // 'industrial' (par catégorie) | 'supplier'
    const [source, setSource] = useState('receptions'); // 'receptions' (reçu réel) | 'orders' (commandé)
    const [cache, setCache] = useState({}); // `${mode}-${source}` -> data
    const [loading, setLoading] = useState(true);
    const [siteSel, setSiteSel] = useState(null); // {site_id: bool}
    const [supplierId, setSupplierId] = useState('');
    const [search, setSearch] = useState('');
    const [hideConso, setHideConso] = useState(false);
    const [sort, setSort] = useState({ key: 'qty_per_1000', dir: -1 });
    const cacheKey = `${mode}-${source}`;
    const data = cache[cacheKey];

    useEffect(() => {
        if (cache[cacheKey]) return;
        setLoading(true);
        orderStatsAPI.productRatios({ year: 2026, group: mode, source })
            .then(r => {
                setCache(c => ({ ...c, [cacheKey]: r.data }));
                // Sites FE cochés par défaut, sauf le Siège (données pré-avril non ventilées, pas de CA)
                setSiteSel(prev => prev || Object.fromEntries(r.data.sites.map(s => [s.site_id, (s.site_name || '').startsWith('FE ') && s.site_name !== 'FE Siège'])));
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, [cacheKey]);

    const visibleSites = useMemo(
        () => (data?.sites || []).filter(s => !allowedIds || allowedIds.includes(s.site_id)),
        [data, allowedIds]
    );

    const rows = useMemo(() => {
        if (!data || !siteSel) return [];
        const q = search.trim().toLowerCase();
        let out = data.rows.filter(r =>
            siteSel[r.site_id]
            && (!allowedIds || allowedIds.includes(r.site_id))
            && (!supplierId || r.supplier_id === supplierId)
            && (!hideConso || !r.is_consumable)
            && (!q || r.product_name.toLowerCase().includes(q) || (r.supplier_name || '').toLowerCase().includes(q) || (r.category || '').toLowerCase().includes(q))
        );
        const { key, dir } = sort;
        out = [...out].sort((a, b) => {
            const va = a[key], vb = b[key];
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            if (typeof va === 'string') return va.localeCompare(vb) * dir;
            return (va - vb) * dir;
        });
        return out;
    }, [data, siteSel, supplierId, search, hideConso, sort, allowedIds]);

    // Regroupement par catégorie (mode produits industriels), trié par dépense décroissante
    const categoryGroups = useMemo(() => {
        if (mode !== 'industrial') return null;
        const map = new Map();
        for (const r of rows) {
            const cat = r.category || 'Sans catégorie';
            if (!map.has(cat)) map.set(cat, { name: cat, rows: [], spent: 0 });
            const g = map.get(cat);
            g.rows.push(r);
            g.spent += r.total_spent || 0;
        }
        return [...map.values()].sort((a, b) => b.spent - a.spent);
    }, [rows, mode]);

    const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: -s.dir } : { key, dir: -1 });
    const COLUMNS = buildColumns(source);

    if (loading) {
        return <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
    }
    if (!data) return null;

    return (
        <div className="space-y-3" data-testid="order-ratios-tab">
            <Card>
                <CardHeader className="py-2 px-3 border-b space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-indigo-500" />
                        <CardTitle className="text-sm">Ratios de commande par produit</CardTitle>
                        <Badge variant="outline" className="text-[10px] font-mono text-slate-500">
                            {data.year} · S1 → S{data.end_week}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]" data-testid="ratios-row-count">{rows.length} lignes</Badge>
                        <div className="inline-flex bg-slate-200 rounded-md p-0.5 text-[11px] gap-0.5">
                            <button type="button" onClick={() => setSource('receptions')}
                                className={`px-2.5 py-0.5 rounded font-semibold transition-all ${source === 'receptions' ? 'bg-emerald-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                data-testid="ratios-source-receptions"
                                title="Quantités réellement reçues (BL réceptions + réceptions manuelles + transferts entrants − sortants)">Reçu (réceptions)</button>
                            <button type="button" onClick={() => setSource('orders')}
                                className={`px-2.5 py-0.5 rounded font-semibold transition-all ${source === 'orders' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                data-testid="ratios-source-orders"
                                title="Quantités commandées via Sokuzai uniquement">Commandé (Sokuzai)</button>
                        </div>
                        <div className="inline-flex bg-slate-200 rounded-md p-0.5 text-[11px] gap-0.5">
                            <button type="button" onClick={() => setMode('industrial')}
                                className={`px-2.5 py-0.5 rounded font-semibold transition-all ${mode === 'industrial' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                data-testid="ratios-mode-industrial">Par catégorie (PI)</button>
                            <button type="button" onClick={() => setMode('supplier')}
                                className={`px-2.5 py-0.5 rounded font-semibold transition-all ${mode === 'supplier' ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                                data-testid="ratios-mode-supplier">Produits fournisseurs</button>
                        </div>
                        <span className="text-[11px] text-slate-400 hidden xl:inline">
                            Base du futur prévisionnel de commande : cadences et quantités rapportées au CA réalisé
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {visibleSites.map(s => (
                            <label key={s.site_id}
                                className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border cursor-pointer text-[11px] font-semibold ${siteSel?.[s.site_id] ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200'}`}
                                data-testid={`ratios-site-${(s.site_name || '').replace('FE ', '').toLowerCase().replace(/\s/g, '-')}`}>
                                <Checkbox checked={!!siteSel?.[s.site_id]} onCheckedChange={() => setSiteSel(v => ({ ...v, [s.site_id]: !v[s.site_id] }))} className="w-3 h-3" />
                                {(s.site_name || '?').replace('FE ', '')}
                                <span className="opacity-60">({s.products})</span>
                            </label>
                        ))}
                        {mode === 'supplier' && (
                        <select value={supplierId} onChange={e => setSupplierId(e.target.value)}
                            className="h-6 border rounded-md px-1.5 text-[11px] bg-white text-slate-600"
                            data-testid="ratios-supplier-filter">
                            <option value="">Tous fournisseurs</option>
                            {data.suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        )}
                        <div className="relative">
                            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un produit…"
                                className="h-6 pl-6 text-[11px] w-48" data-testid="ratios-search" />
                        </div>
                        <label className={`inline-flex items-center gap-1 px-2 h-6 rounded-md border cursor-pointer text-[11px] font-semibold ${hideConso ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-slate-200 text-slate-500'}`}
                            data-testid="ratios-hide-conso">
                            <Checkbox checked={hideConso} onCheckedChange={() => setHideConso(v => !v)} className="w-3 h-3" />
                            Masquer conso/dépannages
                        </label>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                        <table className="w-full text-[11px]">
                            <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500 sticky top-0 z-10">
                                <tr>
                                    {COLUMNS.map(c => (
                                        <th key={c.key}
                                            className={`px-2 py-1.5 whitespace-nowrap cursor-pointer select-none hover:text-slate-800 text-${c.align}`}
                                            title={c.title}
                                            onClick={() => toggleSort(c.key)}
                                            data-testid={`ratios-sort-${c.key}`}>
                                            {c.label}
                                            {sort.key === c.key && <ArrowUpDown className="inline w-2.5 h-2.5 ml-0.5 text-indigo-500" />}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {mode === 'industrial' && categoryGroups ? (
                                    categoryGroups.map(g => (
                                        <React.Fragment key={g.name}>
                                            <tr className="bg-indigo-50/70 border-b border-indigo-100" data-testid={`ratios-category-${g.name.toLowerCase().replace(/[\s.]+/g, '-')}`}>
                                                <td colSpan={COLUMNS.length} className="px-2 py-1 text-[10px] font-bold uppercase text-indigo-700">
                                                    {g.name}
                                                    <span className="font-normal normal-case text-indigo-500 ml-2">
                                                        {g.rows.length} ligne{g.rows.length > 1 ? 's' : ''} · {formatCurrency(Math.round(g.spent))} commandés
                                                    </span>
                                                </td>
                                            </tr>
                                            {g.rows.map(r => <RatioRow key={`${r.site_id}-${r.product_id}`} r={r} />)}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    rows.map(r => <RatioRow key={`${r.site_id}-${r.product_id}`} r={r} />)
                                )}
                                {rows.length === 0 && (
                                    <tr><td colSpan={COLUMNS.length} className="px-3 py-6 text-center text-slate-400 italic">Aucun produit ne correspond aux filtres.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-3 py-1.5 text-[10px] text-slate-400 italic border-t">
                        {source === 'receptions'
                            ? <>Ratio /1000 € CA = quantité <b>réellement reçue</b> (BL réceptions + réceptions manuelles + transferts entrants − sortants) pour 1 000 € de CA réalisé depuis la première réception du produit. Source recommandée : capte les livraisons hebdo sans commande Sokuzai (ex. saumon). Montants estimés au prix catalogue.</>
                            : <>Ratio /1000 € CA = quantité <b>commandée via Sokuzai</b> pour 1 000 € de CA réalisé depuis la première commande du produit. ⚠ Sous-estime les produits livrés sans commande hebdo (ex. saumon) — préférez la vue « Reçu ».</>}
                        {' '}Les consommables (gants, essuie-tout, entretien…) sont des dépannages fournis par Génipa : pas de ratio, exclus de la future auto-proposition.
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
